import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
// import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import bindAllActions from '../../common/bindAllActions';
import SubscriptionTabs from './SubscriptionTabs';
import StepIndicator from './StepIndicator';
import SubscribeProcess from './SubscribeProcess';
import SubscriptionDisplayCard from './SubscriptionDisplayCard';
import PaymentMethodSelector from './PaymentMethodSelector';
import SubscriptionSuccessMessage from './SubscriptionSuccessMessage';
import { isIosApp, isAndroidApp } from '../../common/ambient/utils';
import { isCameraSubscription, isCellularSubscription, isHeatStressSubscription, deviceSubscriptionStatus, subIsPlus, getHeatStressTier, getHeatStressTierLimits } from './index';
import { usePaymentPolling } from './usePaymentPolling';
import { useDeviceIdRedirect } from './useDeviceIdRedirect';
import { findExistingSubscription, checkSubscriptionSuccess as checkSubscriptionSuccessHelper, detectDowngrade, TIER_HIERARCHIES } from './subscriptionHelpers';
import HeatStressDowngradeMemberRemoval from './HeatStressDowngradeMemberRemoval';
import { isPhoneVerified } from '../alert/AlertSetMemberCard.helpers';
import { path } from 'ramda';
import './CheckoutPage.less';

function CheckoutPage({ user, device, userActions, deviceActions }) {
  const location = useLocation();
  const [showPaymentMethodChoice, setShowPaymentMethodChoice] = useState(false);
  const [paymentMethodChosen, setPaymentMethodChosen] = useState(null); // 'iap' or 'cc'
  const [memberRemovalComplete, setMemberRemovalComplete] = useState(false);
  const geoVerificationDoneRef = useRef(false);

  // Payment polling hook
  const { startPolling, stopPolling, isPolling } = usePaymentPolling(deviceActions)
  const { paymentComplete, appRefreshingUser } = device

  // Track initial subscription state to detect changes (for upgrades/downgrades)
  const initialSubscriptionRef = useRef(null)

  // Parse query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      subscription: params.get('subscription'),
      code: params.get('code'),
      redeemCode: params.get('redeemCode') === 'true',
      plusUserSwitch: params.get('plusUserSwitch') === 'true',
      paymentMethod: params.get('paymentMethod'),
      deviceId: params.get('deviceId'),
      showSuccess: params.get('showSuccess') === 'true' // For testing/styling success message
    };
  }, [location.search]);

  // Handle MAC address in deviceId - redirect to proper device ID
  useDeviceIdRedirect(queryParams.deviceId, device);

  // Lookup device if deviceId is provided (for device-specific subscriptions)
  const currentDevice = useMemo(() => {
    if (!queryParams.deviceId || !device?.devices) return null;
    return device.devices.find(d => d._id === queryParams.deviceId);
  }, [queryParams.deviceId, device]);

  const macAddress = currentDevice?.macAddress;
  const isDeviceSubscription = !!queryParams.deviceId;

  // Derive annual from subscription name
  const annual = useMemo(() => {
    if (!queryParams.subscription) return true;
    return queryParams.subscription.includes('-yearly');
  }, [queryParams.subscription]);

  // Derive tier from subscription name
  const tier = useMemo(() => {
    if (!queryParams.subscription) return 'plus';
    return queryParams.subscription.includes('for-teams') ? 'teams' : 'plus';
  }, [queryParams.subscription]);

  // Determine subscription type once
  const isCameraSub = isCameraSubscription(queryParams.subscription);
  const isHeatStressSub = isHeatStressSubscription(queryParams.subscription);

  // Detect heat stress downgrade requiring member removal
  const needsMemberRemoval = useMemo(() => {
    if (!isHeatStressSub || !user?.info) return false

    // Find existing subscription
    const existingSub = findExistingSubscription(user, null, isHeatStressSubscription)
    if (!existingSub) return false

    // Check if it's a downgrade
    const isDowngrade = detectDowngrade(
      TIER_HIERARCHIES.heatStress,
      existingSub,
      queryParams.subscription
    )
    if (!isDowngrade) return false

    // Get tier limits
    const currentTier = getHeatStressTier({ info: user.info })
    const currentLimits = getHeatStressTierLimits(currentTier)

    // Determine new tier from subscription name
    let newTierName = 'free'
    if (queryParams.subscription.includes('enterprise')) newTierName = 'enterprise'
    else if (queryParams.subscription.includes('pro')) newTierName = 'pro'
    else if (queryParams.subscription.includes('standard')) newTierName = 'standard'
    const newLimits = getHeatStressTierLimits(newTierName)

    // Count current SMS members (members with verified phones)
    const members = user.info.heatStressGroupMembers || []
    const userObj = { info: user.info }

    // Count non-admin members with verified phones
    const smsMembers = members.filter(m => !m.admin && m.phone && isPhoneVerified(m, userObj))

    // Count admin separately (only if they have a verified phone)
    const adminMember = members.find(m => m.admin)
    const adminHasVerifiedPhone = adminMember && isPhoneVerified(adminMember, userObj)
    const totalSmsMembers = (adminHasVerifiedPhone ? 1 : 0) + smsMembers.length

    // Return true if over new limit
    return totalSmsMembers > newLimits.maxMembers
  }, [isHeatStressSub, user, queryParams.subscription])

  // Determine if we should show payment method selection (basic check)
  // Only show for app users and when no code/gift redemption
  // Skip payment method selection for device subscriptions (cameras don't support IAP)
  // Skip payment method selection for Heat Stress subscriptions (account-level, web-only)
  // For Android users, additional geolocation verification happens in useEffect
  const shouldShowPaymentMethodChoiceBasic = (isIosApp() || isAndroidApp()) && !queryParams.code && !queryParams.redeemCode && !isDeviceSubscription && !isHeatStressSub;

  // Helper: Find camera subscription for a device
  const findCameraSubscription = useCallback((macAddr, productName = null) => {
    if (productName) {
      // Looking for specific product
      const subscriptions = path(['info', 'payments', 'subscriptions'], user) || [];
      return subscriptions.find(sub => sub.macAddress === macAddr && sub.productName === productName);
    }
    // Looking for any camera subscription
    return findExistingSubscription(user, macAddr, isCameraSubscription);
  }, [user]);

  // Helper: Find heat stress subscription
  const findHeatStressSubscription = useCallback((productName) => {
    const subscriptions = path(['info', 'payments', 'subscriptions'], user) || [];
    return subscriptions.find(sub => sub.productName === productName);
  }, [user]);

  // Helper: Capture initial subscription state
  const captureInitialSubscriptionState = useCallback(() => {
    let existingSub = null;

    if (isCameraSub && macAddress) {
      existingSub = findCameraSubscription(macAddress);
    } else if (isHeatStressSub) {
      existingSub = findExistingSubscription(user, null, isHeatStressSubscription);
    } else {
      // AWN+ subscription
      existingSub = findExistingSubscription(user, null, (productName) => {
        const subscriptions = path(['info', 'payments', 'subscriptions'], user) || [];
        const sub = subscriptions.find(s => s.productName === productName);
        return sub ? subIsPlus(sub) : false;
      });
    }

    return existingSub ? {
      productName: existingSub.productName,
      willDowngradeTo: existingSub.willDowngradeTo
    } : null;
  }, [isCameraSub, isHeatStressSub, macAddress, user, findCameraSubscription]);

  // Helper: Check if subscription is successful
  const checkSubscriptionSuccess = useCallback(() => {
    // Use universal helper for camera and heat stress subscriptions
    if (isCameraSub || isHeatStressSub) {
      return checkSubscriptionSuccessHelper(
        user,
        queryParams.subscription,
        macAddress || null,
        initialSubscriptionRef.current
      );
    }

    // AWN+ subscription - use universal helper
    const result = checkSubscriptionSuccessHelper(
      user,
      queryParams.subscription,
      null,
      initialSubscriptionRef.current
    );

    // If universal helper found success, return true
    if (result) return true;

    // Legacy check for backwards compatibility with plusUserSwitch
    const currentSub = user.plus;
    if (!currentSub) return false;

    if (!initialSubscriptionRef.current) {
      return true;
    } else if (queryParams.plusUserSwitch && currentSub.productName === queryParams.subscription) {
      return true;
    }

    return false;
  }, [isCameraSub, isHeatStressSub, macAddress, queryParams.subscription, queryParams.plusUserSwitch, user]);

  // Initialize payment method choice state with Android geolocation verification
  React.useEffect(() => {
    const initializePaymentMethod = async () => {
      if (shouldShowPaymentMethodChoiceBasic && !paymentMethodChosen && !geoVerificationDoneRef.current) {
        let showPaymentChoice = true;

        // Android geolocation verification
        if (isAndroidApp() && device?.countryCode?.code) {
          const deviceCountryCode = device.countryCode.code;

          if (!['US'].includes(deviceCountryCode)) {
            // Device country code is not US
            showPaymentChoice = false;
            console.log(deviceCountryCode + ' is not US');
          } else {
            // Device country code is US, verify with IP lookup
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 3000);

              const response = await fetch('https://geolocation-db.com/json/', {
                signal: controller.signal
              });
              clearTimeout(timeoutId);

              const ipData = await response.json();
              const ipCountryCode = ipData?.country_code;

              if (ipCountryCode !== 'US') {
                // IP country code doesn't match or couldn't be retrieved
                showPaymentChoice = false;
                console.log('IP country code mismatch or unavailable:', ipCountryCode);
              }
            } catch (err) {
              console.log('Failed to verify IP country code:', err);
              showPaymentChoice = false;
            }
          }
        }

        // Mark verification as complete
        geoVerificationDoneRef.current = true;
        setShowPaymentMethodChoice(showPaymentChoice);
      } else if (queryParams.paymentMethod) {
        // If payment method is passed in query params, use it
        setPaymentMethodChosen(queryParams.paymentMethod);
        setShowPaymentMethodChoice(false);
      }
    };

    initializePaymentMethod();
  }, [shouldShowPaymentMethodChoiceBasic, queryParams.paymentMethod, paymentMethodChosen, device]);

  // Reset payment complete flag and capture initial subscription state on mount
  useEffect(() => {
    deviceActions.setThing('paymentComplete', false);
    initialSubscriptionRef.current = captureInitialSubscriptionState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for subscription success while polling
  useEffect(() => {
    if (!isPolling()) return;

    if (checkSubscriptionSuccess()) {
      stopPolling(true);
    }
  }, [isPolling, stopPolling, checkSubscriptionSuccess]);

  // Reset member removal complete when subscription changes
  useEffect(() => {
    setMemberRemovalComplete(false);
  }, [queryParams.subscription]);

  // Reset geo verification when subscription changes
  useEffect(() => {
    geoVerificationDoneRef.current = false;
  }, [queryParams.subscription]);

  const handleMethodSelected = (method) => {
    if (method === 'gift-code') {
      // Open gift code modal instead of proceeding to checkout
      userActions.doModal({ type: 'gift-code' });
      // Don't close payment method selection - modal will handle the redirect
    } else {
      setPaymentMethodChosen(method);
      setShowPaymentMethodChoice(false);
    }
  };

  // Show success message if payment is complete OR showSuccess flag is set (for styling/testing)
  if (paymentComplete || queryParams.showSuccess) {
    return (
      <div className="payment-checkout-page">
        <header className='main'>
          <h1>Subscription Confirmed</h1>
        </header>
        <SubscriptionTabs currentTab="manage" />
        <div className='page-body'>
          <SubscriptionSuccessMessage
            user={user}
            subscription={queryParams.subscription}
            currentDevice={currentDevice}
            appRefreshingUser={appRefreshingUser}
            userActions={userActions}
          />
        </div>
      </div>
    );
  }

  // If no subscription specified, redirect back
  if (!queryParams.subscription) {
    return (
      <div className="payment-checkout-page">
        <header className='main'>
          <h1>Review and Pay</h1>
        </header>
        <SubscriptionTabs currentTab="manage" />
        <div className='page-body'>
          <div className='block'>
            <p>No subscription selected. Please <Link to='/subscriptions/subscribe'>select a plan</Link>.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show member removal UI if downgrade requires it
  if (needsMemberRemoval && !memberRemovalComplete) {
    // Calculate tiers and limits
    const existingSub = findExistingSubscription(user, null, isHeatStressSubscription);
    const currentTier = getHeatStressTier({ info: user.info });
    const currentLimits = getHeatStressTierLimits(currentTier);

    let newTierName = 'free';
    if (queryParams.subscription.includes('enterprise')) newTierName = 'enterprise';
    else if (queryParams.subscription.includes('pro')) newTierName = 'pro';
    else if (queryParams.subscription.includes('standard')) newTierName = 'standard';
    const newLimits = getHeatStressTierLimits(newTierName);

    return (
      <div className="payment-checkout-page">
        <header className='main'>
          <h1>Review and Downgrade</h1>
        </header>
        <SubscriptionTabs currentTab="manage" />
        <div className='page-body'>
          <div className='block'>
            <StepIndicator currentStep={2} cancelLink='/subscriptions' />
            <HeatStressDowngradeMemberRemoval
              user={user}
              userActions={userActions}
              subscription={queryParams.subscription}
              currentTier={currentTier}
              newTier={newTierName}
              currentMaxMembers={currentLimits.maxMembers}
              newMaxMembers={newLimits.maxMembers}
              onDowngradeComplete={() => setMemberRemovalComplete(true)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Show payment method selection if needed
  if (showPaymentMethodChoice) {
    return (
      <div className="payment-checkout-page">
        <header className='main'>
          <h1>Choose Payment Method</h1>
        </header>
        <SubscriptionTabs currentTab="manage" />
        <div className='page-body'>
          <div className='block'>
            <StepIndicator currentStep={2} cancelLink='/subscriptions' />
            <PaymentMethodSelector
              selectedTier={tier}
              annual={annual}
              onMethodSelected={handleMethodSelected}
              user={user}
              device={device}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-checkout-page">
      <header className='main'>
        <h1>Review and Pay</h1>
      </header>
      <SubscriptionTabs currentTab="manage" />
      <div className='page-body'>
        <div className='block'>
          <StepIndicator currentStep={2} cancelLink='/subscriptions' />
          <div className='body'>
            <SubscriptionDisplayCard subscription={queryParams.subscription} />
            <SubscribeProcess
              user={user}
              userActions={userActions}
              subscription={queryParams.subscription}
              code={queryParams.code}
              redeemCode={queryParams.redeemCode}
              plusUserSwitch={queryParams.plusUserSwitch}
              paymentMethod={paymentMethodChosen === 'cc' ? 'stripe' : paymentMethodChosen === 'iap' ? 'iap' : queryParams.paymentMethod || 'auto'}
              useDiscountedPricing={paymentMethodChosen === 'cc' && !user.plus}
              forceIAP={paymentMethodChosen === 'iap'}
              currentDevice={currentDevice}
              macAddress={macAddress}
              startPolling={startPolling}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default bindAllActions(CheckoutPage);

CheckoutPage.propTypes = {};
CheckoutPage.defaultProps = {};

CheckoutPage.displayName = 'CheckoutPage'
